import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

export function WeightConfigDisplay({ weightConfig, boatName }) {
    const { t } = useTranslation()

    if (!weightConfig) {
        return null
    }

    return (
        <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {t('Weight Configuration')} - {boatName}
                </h3>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Capacity Section */}
                <div className="bg-white p-2 rounded-md border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 text-center">
                        {t('Capacity')}
                    </h4>
                    <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Passengers')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.capacity?.passengers || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Crew')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.capacity?.crew || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Captains')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.capacity?.captains || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Weight Standards Section */}
                <div className="bg-white p-2 rounded-md border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 text-center">
                        {t('Weight Standards')}
                    </h4>
                    <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Infant')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.weight_standards?.passengers?.infant_kg || 'N/A'}kg</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Child')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.weight_standards?.passengers?.child_kg || 'N/A'}kg</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Adult')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.weight_standards?.passengers?.adult_kg || 'N/A'}kg</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Senior')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.weight_standards?.passengers?.senior_kg || 'N/A'}kg</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Default Luggage')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.weight_standards?.default_luggage_kg || 'N/A'}kg</span>
                        </div>
                    </div>
                </div>

                {/* Luggage Policy Section */}
                <div className="bg-white p-2 rounded-md border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 text-center">
                        {t('Luggage Policy')}
                    </h4>
                    <div className="space-y-1">
                        <div className="text-xs">
                            <span className="text-gray-600">{t('Extra Luggage Options')}:</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {weightConfig.luggage_policy?.extra_luggage_options_kg?.map((option, index) => (
                                    <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded">
                                        {option}kg
                                    </span>
                                )) || <span className="text-gray-500">N/A</span>}
                            </div>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Max Extra Luggage')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.luggage_policy?.max_extra_luggage_total_kg || 'N/A'}kg</span>
                        </div>
                    </div>
                </div>

                {/* Safety Limits Section */}
                <div className="bg-white p-2 rounded-md border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 text-center">
                        {t('Safety Limits')}
                    </h4>
                    <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Max Passenger Weight')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.safety_limits?.max_passenger_weight_kg || 'N/A'}kg</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Max Load')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.safety_limits?.max_load_kg || 'N/A'}kg</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Strollers')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.special_items_limits?.strollers || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600">{t('Wheelchairs')}:</span>
                            <span className="font-semibold text-gray-900">{weightConfig.special_items_limits?.wheelchairs || 'N/A'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

WeightConfigDisplay.propTypes = {
    weightConfig: PropTypes.object,
    boatName: PropTypes.string,
}
